import React, { useState } from 'react';
import { Table, Column } from '@appkit4/react-components/table';
import { Loading } from '@appkit4/react-components/loading';
import { Pagination } from '@appkit4/react-components/pagination';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import { Button } from '@appkit4/react-components';
import useLocale from '../../locale/useLocale';
import { useGetV1BusinessRule } from '../../api/generated/endpoint';
import { UploadValidationRule } from '../../api/generated/models';
import usePaginationControl from '../../helpers/usePaginationControl';
import useTitle from '../../helpers/useTitle';

import './AdminPanel.scss';
import SuperAdminCreateBusinessRuleModal from '../../components/SuperAdmin/SuperAdminCreateBusinessRuleModal';
import SuperAdminEditBusinessRuleModal from '../../components/SuperAdmin/SuperAdminEditBusinessRuleModal';
import BusinessRuleManagementActionsCell from '../../components/SuperAdmin/BusinessRuleManagementActionsCell';
import SuperAdminConfirmDeleteBusinessRuleModal from '../../components/SuperAdmin/SuperAdminConfirmDeleteBusinessRuleModal';

const BusinessRuleManagement = () => {
    const { l } = useLocale();
    useTitle(l('_businessUnitManagementTitleCase'));

    const [displayCreateBusinessRuleModal, setDisplayCreateBusinessRuleModal] =
        useState(false);

    const [
        displayEditBusinessRuleModalForId,
        setDisplayEditBusinessRuleModalForId,
    ] = useState<number | undefined>(undefined);

    const [
        displayDeleteBusinessRuleModalForId,
        setDisplayDeleteBusinessRuleModalForId,
    ] = useState<number | undefined>(undefined);

    const pageSize = 20;
    const [pageNum, setPageNum] = useState<number>(1);

    const skip = (pageNum - 1) * pageSize;
    const take = pageSize;

    const showGenericErrorToast = useGenericErrorToast();

    const { data: paginatedBusinessRules, isLoading: loadingBusinessRules } =
        useGetV1BusinessRule(
            { skip, take },
            {
                query: {
                    onError: showGenericErrorToast,
                },
            }
        );

    /* prevent current page number being invalid */
    usePaginationControl(
        { totalCount: paginatedBusinessRules?.totalBusinessRuleCount },
        pageSize,
        setPageNum,
        pageNum
    );

    const findBusinessRuleById = (id: number) =>
        paginatedBusinessRules?.businessRules.find((t) => t.id == id);

    const actionsColumnStyle: React.CSSProperties = {
        width: '6rem',
    };

    return (
        <>
            <div className="row mt-4">
                <div className="col">
                    <Button
                        kind="primary"
                        icon="icon-plus-outline"
                        type="button"
                        role="button"
                        onClick={() => setDisplayCreateBusinessRuleModal(true)}
                        className="ms-auto align-top h-100"
                        style={{ paddingTop: '12px' }}
                    >
                        {l('_addBusinessRule')}
                    </Button>
                </div>
            </div>

            {displayCreateBusinessRuleModal && (
                <SuperAdminCreateBusinessRuleModal
                    isVisible
                    setIsVisible={setDisplayCreateBusinessRuleModal}
                />
            )}

            {displayEditBusinessRuleModalForId != undefined && (
                <SuperAdminEditBusinessRuleModal
                    isVisible
                    setIsVisible={() =>
                        setDisplayEditBusinessRuleModalForId(undefined)
                    }
                    existingRule={findBusinessRuleById(
                        displayEditBusinessRuleModalForId
                    )}
                />
            )}

            {displayDeleteBusinessRuleModalForId != undefined && (
                <SuperAdminConfirmDeleteBusinessRuleModal
                    visible
                    setNotVisible={() =>
                        setDisplayDeleteBusinessRuleModalForId(undefined)
                    }
                    businessRule={findBusinessRuleById(
                        displayDeleteBusinessRuleModalForId
                    )}
                />
            )}

            <Table
                className="ap-mt-spacing-4 admin-table-container"
                originalData={paginatedBusinessRules?.businessRules ?? []}
                condensed
                striped
                hasTitle
            >
                <Column field="name">{l('_ruleAndStorageName')}</Column>
                <Column field="rules">{l('_businessRules')}</Column>
                <Column
                    renderCell={(row: UploadValidationRule) => (
                        <BusinessRuleManagementActionsCell
                            businessRule={row}
                            displayEditBusinessRuleModal={() =>
                                setDisplayEditBusinessRuleModalForId(row.id)
                            }
                            displayDeleteBusinessRuleModal={() =>
                                setDisplayDeleteBusinessRuleModalForId(row.id)
                            }
                        />
                    )}
                    field="id"
                    style={actionsColumnStyle}
                >
                    {l('_actions')}
                </Column>
            </Table>

            {!loadingBusinessRules &&
                paginatedBusinessRules &&
                paginatedBusinessRules.businessRules.length > 0 && (
                    <Pagination
                        data-testid="pagination"
                        total={Math.ceil(
                            paginatedBusinessRules.totalBusinessRuleCount /
                                pageSize
                        )}
                        current={pageNum}
                        onPageChange={(page: number) => setPageNum(page)}
                        style={{ float: 'right' }}
                    />
                )}

            {loadingBusinessRules && (
                <Loading loadingType="circular" indeterminate compact={false} />
            )}

            {/* Display message if no businessUnits found from search */}
            {!loadingBusinessRules &&
                (!paginatedBusinessRules ||
                    paginatedBusinessRules.businessRules.length == 0) && (
                    <h5 className="text-center mt-2 ap-text-states-error-03">
                        {l('_noBusinessUnitsFound')}
                    </h5>
                )}
        </>
    );
};

export default BusinessRuleManagement;
